import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KalDialogConfig, KalDialogService, KalSnackbarConfig, KalSnackbarService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import { EMPTY, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { SubscriptionManager } from 'src/app/_core/tools/subscription-manager';
import { ViewerDocumentDialogComponent, ViewerDocumentDialogData } from 'src/app/_shared/dialogs/viewer-document-dialog/viewer-document-dialog.component';
import { UserState } from 'src/app/_user/store/user/user.state';
import { CodesReports } from 'src/environments/codes-reports';
import { constants } from 'src/environments/constants';
import { environment } from 'src/environments/environment';
import { ContextInfosInput, MutationGetListeCourrierGeneriqueArgs } from 'src/graphql/generated-types';

export interface ImpressionReport {
    session: string;

    format: string;

    reports: Report[];
}

export interface Report {
    code: string;

    parameters: ReportParameter[];
}

export interface ReportParameter {
    name: string;
    value: string;
}

/**
 * Crée une url depuis une chaine de caractère en base 64
 * @param base64String fichier encodé en base64
 * @param fileType le MIME du fichier à créer (pdf -> application/pdf)
 */
const convertBase64StringToUrl = (base64String, fileType): string => {
    const byteString = atob(base64String);
    const byteArrays = [];
    const sliceSize = 512;

    // On parcours la chaine de caractère du fichier par tranche de 512
    for (let offset = 0; offset < byteString.length; offset += sliceSize) {
        const slice = byteString.slice(offset, offset + sliceSize);

        // On récupère la valeur de chaque octet à partir du caractère qui le représente
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        byteArrays.push(new Uint8Array(byteNumbers));
    }
    // On crée une url à partir d'un blob généré avec les tableau d'octet
    return URL.createObjectURL(new Blob(byteArrays, {type: fileType}));
};

/**
 * Service relayant les requêtes d'impressions vers l'api de JN
 */
@Injectable({
    providedIn: 'root'
})
export class ReportPrintService extends SubscriptionManager {

    /**
     * Cache des ID courrier par code de report
     * Évite les appels multiples à GetListeCourrierGenerique pour le même code
     */
    private readonly courrierIdCache = new Map<string, Observable<number>>();

    constructor(private store: Store,
                private snackBarService: KalSnackbarService,
                private graphqlService: GraphqlService,
                private http: HttpClient,
                private readonly dialogService: KalDialogService) {
        super();
    }

    private get printApiUrl(): string {
        return environment.printApi + '/impression';
    }

    private get context(): ContextInfosInput {
        return this.store.selectSnapshot(UserState.userContext);
    }

    private get session(): string {
        return this.context.NumSession;
    }

    /**
     * Récupère les images d'un pdf sans les sauvegarder sur le serveur
     */
    printImg(report: Report, {removeCourrierId} = {removeCourrierId: false}): Observable<string[]> {
        return this.getImg(report, {print: true, removeCourrierId});
    }

    /**
     * Récupère les images d'un pdf et les sauvegarde sur le serveur
     */
    saveImg(report: Report, {removeCourrierId} = {removeCourrierId: false}): Observable<string[]> {
        return this.getImg(report, {print: false, removeCourrierId});
    }

    /**
     * Récupère les pdf sans les sauvegarder sur le serveur
     */
    printPdf(reports: Report[], {removeCourrierId} = {removeCourrierId: false}): Observable<string[]> {
        return this.getPdf(reports, {print: true, removeCourrierId});
    }

    /**
     * Récupère les xls sans les sauvegarder sur le serveur
     */
    printXLS(reports: Report[], {removeCourrierId} = {removeCourrierId: false}): Observable<string[]> {
        return this.getXLS(reports, {print: true, removeCourrierId});
    }

    /**
     * Récupère les pdf et les sauvegarde sur le serveur
     */
    savePdf(reports: Report[], {removeCourrierId} = {removeCourrierId: false}): Observable<string[]> {
        return this.getPdf(reports, {print: false, removeCourrierId});
    }

    private getImg(report: Report, {print, removeCourrierId} = {
        print: true,
        removeCourrierId: false
    }): Observable<string[]> {
        return this.buildReport(report, removeCourrierId).pipe(
            this.untilDestroyed,
            switchMap((buildedReport: Report) => {
                return this.print({
                    format: 'IMAGE',
                    reports: [buildedReport],
                    session: this.session
                }, {print});
            })
        );
    }

    private getXLS(reports: Report[], { print, removeCourrierId } = {
        print: false,
        removeCourrierId: false
    }): Observable<string[]> {
        return forkJoin(reports.map(report => this.buildReport(report, removeCourrierId))).pipe(
            this.untilDestroyed,
            switchMap((reportsList: Report[]) => this.print({
                format: constants.fileFormatTypes.XLS,
                reports: reportsList,
                session: this.session
            }, { print }))
        );
    }

    private getPdf(reports: Report[], {print, removeCourrierId} = {
        print: false,
        removeCourrierId: false
    }): Observable<string[]> {
        return forkJoin(reports.map(report => this.buildReport(report, removeCourrierId))).pipe(
            this.untilDestroyed,
            switchMap((reportsList: Report[]) => this.print({
                format: constants.fileFormatTypes.PDF,
                reports: reportsList,
                session: this.session
            }, {print}))
        );
    }

    private buildReport(report: Report, removeCourrierId: boolean): Observable<Report> {
        if (removeCourrierId) {
            return of(report);
        }
        return this.getIdCourrier(report.code).pipe(
                map(idCourrier => {
                    const parameters = this.addMandatoryParameters(report.parameters, {
                        courrierId: idCourrier,
                        sessionNumber: this.session
                    });
                    return {code: report.code, parameters};
                })
            );
    }

    private addMandatoryParameters(parameters: ReportParameter[],
                                   mandatoryParameters: { courrierId: number, sessionNumber: string }): ReportParameter[] {
        if (!parameters.some(p => p.name.toLowerCase() === 'idcourrier')) {
            parameters.unshift({name: 'idCourrier', value: '' + mandatoryParameters.courrierId});
        }
        if (!parameters.some(p => p.name.toLowerCase() === 'numsession')) {
            parameters.unshift({name: 'numSession', value: mandatoryParameters.sessionNumber});
        }
        return parameters;
    }

    /**
     * Retourne un observable contenant l'id de courrier correspondant à un code de report
     * Utilise un cache pour éviter les appels multiples pour le même code
     */
    getIdCourrier(reportCode: string): Observable<number> {
        const cached = this.courrierIdCache.get(reportCode);
        if (cached) {
            return cached;
        }

        const courrierId$ = this.graphqlService.mutateOne<'GetListeCourrierGenerique', MutationGetListeCourrierGeneriqueArgs>({
            mutation: gql`
                mutation GetListeCourrierGenerique($contexte: ContextInfosInput,
                    $criteres: CriteresCourrierDTOInput) {
                    GetListeCourrierGenerique(contexte: $contexte, criteres: $criteres) {
                        CourrierDTO {
                            Id
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    CodeReport: reportCode,
                }
            }
        }).pipe(
            this.untilDestroyed,
            map((GetListeCourrierGenerique) => GetListeCourrierGenerique?.CourrierDTO?.[0]?.Id),
            shareReplay(1)
        );

        this.courrierIdCache.set(reportCode, courrierId$);
        return courrierId$;
    }

    /**
     * Vide le cache des ID courrier (à appeler lors de la déconnexion)
     */
    clearCourrierIdCache(): void {
        this.courrierIdCache.clear();
    }

    private print(impression: ImpressionReport, {print}: { print: boolean }): Observable<string[]> {

        // ajout automatique du numSession dans les paramètres
        impression.reports
            .filter(r => r.parameters.every(p => p.name !== 'numSession')) // aucun paramètre ne représente numSession
            .forEach(r => r.parameters.push({name: 'numSession', value: this.session})); // on ajoute numSession

        const fileType = impression.format === constants.fileFormatTypes.PDF ? 'application/pdf' : 'image/png';
        return this.http.post<string[]>(this.printApiUrl + (print ? '/print' : '/save'), impression).pipe(
            map(prints => {
                if (print) {
                    return prints.map(p => convertBase64StringToUrl(p, fileType));
                }
                return prints;
            }),
            catchError(() => {
                this.snackBarService.open(new KalSnackbarConfig({title: 'Erreur lors de la préparation de l\'impression'}));
                return EMPTY;
            })
        );
    }

    /**
     * Récupère le contenu (base64) des pdf sans les sauvegarder sur le serveur
     */
    getPdfContent$(reports: Report[]): Observable<string[]> {
        return forkJoin(reports.map(report => this.buildReport(report, false))).pipe(
            switchMap((reportsList: Report[]) => {
                reportsList
                    .filter(r => r.parameters.every(p => p.name !== 'numSession'))
                    .forEach(r => r.parameters.push({name: 'numSession', value: this.session}));

                return this.http.post<string[]>(this.printApiUrl + '/print', {
                    format: constants.fileFormatTypes.PDF,
                    reports: reportsList,
                    session: this.session
                });
            })
        );
    }

    getFile$(reports: Report[], format: string): Observable<string[]> {
        return forkJoin(reports.map(report => this.buildReport(report, false))).pipe(
            switchMap((reportsList: Report[]) => this.export$({
                format,
                reports: reportsList,
                session: this.session
            })),
            catchError((error: any) => {
                return throwError(() => error);
            })
        );
    }

    export$(impression: ImpressionReport): Observable<string[]> {

        impression.reports
            .filter(r => r.parameters.every(p => p.name !== 'numSession'))
            .forEach(r => r.parameters.push({ name: 'numSession', value: this.session }));

        return this.http.post<string[]>(this.printApiUrl + '/save', impression).pipe(
            catchError((error: any) => {
                return throwError(() => error);
            })
        );
    }

    printBankDeposit$(bankDepositId: number): Observable<ViewerDocumentDialogData> {
        const { NumSession, CurrentCE } = this.store.selectSnapshot(UserState.userContext);

        const viewerConfig = new KalDialogConfig<ViewerDocumentDialogData>({
            title: 'Impression de la remise en banque',
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                viewerDocumentsList: [{
                    report: {
                        code: CodesReports.REM,
                        parameters: [
                            { name: 'numSession', value: NumSession },
                            { name: 'idCe', value: '' + CurrentCE },
                            { name: 'idRemiseBanque', value: bankDepositId + '' }
                        ]
                    },
                    fileName: 'remise_en_banque'
                }]
            }
        });
        return this.dialogService.open(ViewerDocumentDialogComponent, viewerConfig).afterClosed;
    }

}
